import cx from 'classnames';
import { useId } from 'react';

import { Icon, IconClose, IconKebab } from '@flixbus/honeycomb-icons-react';
import { Button, Divider, Tag, Tooltip } from '@flixbus/honeycomb-react';

import { useBoolean } from 'hooks/useBoolean';
import EditAssignmentFlat from 'pages/WorkAssignments/components/EditAssignmentFlat';
import PopUpEditAssignment from 'pages/WorkAssignments/components/PopUpEditAssignment';
import WorkAssignmentActionMenu from 'pages/WorkAssignments/components/WorkAssignmentActionMenu';
import { getActivityActions } from 'pages/WorkAssignments/components/WorkAssignmentActionMenu/workAssignmentActionMenuFactory';
import { calculateDuration, formatDateDisplay, formatTime } from 'utils/datetime';

import DriverInfo from './DriverInfo';
import { LABELS } from './labels';
import RunJobRoute from './RunJobRoute';
import Stations from './Stations';
import { useReconciliation } from './useReconciliation';
import {
	buildAssignmentLabel,
	deriveSequenceData,
	getRouteText,
	tagAppearance,
	titleCase,
} from './utils';

import styles from './styles.module.css';

const noop = () => {};

const WorkAssignmentCard = ({
	assignment,
	onCopyRideId,
	isInWidgetMode = false,
	isActionMenuActive = false,
	closeWidget = noop,
	onReconcileSuccess = noop,
	onActionMenuToggle = noop,
	onShowRelatedAssignments = noop,
}) => {
	const popupFormId = useId();
	const popupId = useId();
	const tooltipId = useId();
	const [isExpanded, { toggle: toggleExpanded }] = useBoolean();
	const [isPopupOpen, { setToTrue: openPopup, setToFalse: closePopup }] = useBoolean();

	const sequences = assignment?.workAssignmentSequences ?? [];
	const {
		plannedDeparture,
		plannedArrival,
		ridePlannedDeparture,
		ridePlannedArrival,
		plannedAssignmentStart,
		plannedAssignmentEnd,
		stations,
		stationActuals,
		rideItems,
		extraActivityItems,
		firstStation,
		lastStation,
		departureTimezone,
		arrivalTimezone,
		firstSequence,
		lastSequence,
		sequenceGroupId,
		firstSequenceWorkAssignment,
		lastSequenceWorkAssignment,
	} = deriveSequenceData({ sequences, assignment });

	const {
		isLoading: isPopupLoading,
		popupDefaultValues,
		isActionMenuLoading,
		actionCallbacks,
		handlePopupSubmit,
	} = useReconciliation({
		assignment,
		plannedDeparture,
		plannedArrival,
		firstStation,
		lastStation,
		firstSequence,
		lastSequence,
		firstSequenceWorkAssignment,
		lastSequenceWorkAssignment,
		sequenceGroupId,
		departureTimezone,
		arrivalTimezone,
		onReconcileSuccess,
		onPopupClose: closePopup,
		onShowRelatedAssignments,
		onManualReconciliationSuccess: openPopup,
	});

	if (!assignment) return null;

	const actionMenuItems = getActivityActions(
		{ ...assignment, plannedDeparture, plannedArrival, ridePlannedDeparture },
		actionCallbacks
	);
	const isProblematic = Object.hasOwn(assignment, 'isProblematic')
		? assignment.isProblematic
		: true;
	const assignmentLabel = buildAssignmentLabel(stations);
	const duration = calculateDuration(plannedDeparture, plannedArrival, true);
	const reconciliation = titleCase(assignment.reconciliationStatus);
	const routeText = getRouteText(sequences);
	const driverName = assignment.crewMember?.fullName ?? 'N/A';
	const driverId = assignment.crewMember?.driverCard ?? 'N/A';
	const runName = assignment.run?.name ?? 'N/A';
	const jobCode = assignment.job?.code ?? 'N/A';

	return (
		<div
			className={cx(styles.container, {
				[styles.problematic]: isProblematic,
				[styles.widget]: isInWidgetMode,
			})}
		>
			<div className={styles.closeWidget}>
				<Button aria-label="Close widget" onClick={closeWidget} size="sm" display="square">
					<Icon InlineIcon={IconClose} />
				</Button>
			</div>
			{!isInWidgetMode && isPopupOpen && (
				<PopUpEditAssignment
					popupId={popupId}
					formId={popupFormId}
					loading={isPopupLoading}
					defaultValues={popupDefaultValues}
					stationLabel={assignmentLabel}
					onClose={closePopup}
					onSubmit={handlePopupSubmit}
				/>
			)}
			<div className={cx({ [styles.disabled]: !isInWidgetMode })}>
				<RunJobRoute runName={runName} jobCode={jobCode} routeText={routeText} />
			</div>
			<div
				role="button"
				aria-label="Toggle assignment details"
				aria-expanded={isExpanded}
				tabIndex={0}
				data-testid="summary-row"
				className={styles.summaryRow}
				onClick={toggleExpanded}
			>
				<div className={cx(styles.block, styles.routeBlock, { [styles.disabled]: isInWidgetMode })}>
					<RunJobRoute runName={runName} jobCode={jobCode} routeText={routeText} />
				</div>
				<div className={styles.infoStatColumn}>
					<div className={styles.infoStatRow}>
						<div className={cx(styles.block, styles.infoStatItemWide)}>
							<div className={styles.rideLabel}>{LABELS.ridePlannedDeparture}</div>
							<div>
								{ridePlannedDeparture
									? `${formatDateDisplay(ridePlannedDeparture, { timezone: departureTimezone })} ${formatTime(ridePlannedDeparture, { timezone: departureTimezone })}`
									: LABELS.notAvailable}
							</div>
						</div>
						<div className={cx(styles.block, styles.infoStatItemWide)}>
							<div className={styles.rideLabel}>{LABELS.ridePlannedArrival}</div>
							<div>
								{ridePlannedArrival
									? `${formatDateDisplay(ridePlannedArrival, { timezone: arrivalTimezone })} ${formatTime(ridePlannedArrival, { timezone: arrivalTimezone })}`
									: LABELS.notAvailable}
							</div>
						</div>
					</div>
					<Tooltip
						openOnHover
						id={tooltipId}
						position="top"
						size="content-fit"
						wrapAround="block"
						content={
							extraActivityItems.length > 0 ? (
								<div className={styles.tooltipContent}>
									<div className={styles.tooltipTitle}>{LABELS.extraActivitiesTitle}</div>
									<ul className={styles.tooltipList}>
										{extraActivityItems.map((item) => (
											<li key={item.key}>
												{item.label}
												{item.stationCode ? ` (${item.stationCode})` : ''}
												{` — Trip: ${item.tripNumber}`}
												{item.durationMinutes !== null ? ` — ${item.durationMinutes} min` : ''}
											</li>
										))}
									</ul>
								</div>
							) : (
								LABELS.noExtraActivities
							)
						}
					>
						<div
							className={cx(styles.infoStatRow, styles.infoStatRowAssignment)}
							onClick={(e) => e.stopPropagation()}
						>
							<div className={cx(styles.block, styles.infoStatItemWide)}>
								<div className={styles.rideLabel}>{LABELS.assignmentStart}</div>
								<div>
									{plannedAssignmentStart
										? `${formatDateDisplay(plannedAssignmentStart, { timezone: departureTimezone })} ${formatTime(plannedAssignmentStart, { timezone: departureTimezone })}`
										: LABELS.notAvailable}
								</div>
							</div>
							<div className={cx(styles.block, styles.infoStatItemWide)}>
								<div className={styles.rideLabel}>{LABELS.assignmentEnd}</div>
								<div>
									{plannedAssignmentEnd
										? `${formatDateDisplay(plannedAssignmentEnd, { timezone: arrivalTimezone })} ${formatTime(plannedAssignmentEnd, { timezone: arrivalTimezone })}`
										: LABELS.notAvailable}
								</div>
							</div>
						</div>
					</Tooltip>
				</div>
				<div className={cx(styles.block, styles.infoStatItem)}>
					<div className={styles.label}>{LABELS.duration}</div>
					<div className={styles.value}>{duration}</div>
				</div>
				<div className={cx(styles.block, styles.infoStatItem)}>
					<div className={styles.label}>{LABELS.reconciliation}</div>
					<Tag
						small
						extraClasses={styles.tag}
						display="subtle"
						appearance={tagAppearance(assignment?.reconciliationStatus)}
					>
						{reconciliation}
					</Tag>
				</div>
				<div className={cx(styles.block, styles.name, { [styles.disabled]: isInWidgetMode })}>
					<DriverInfo
						crewMember={assignment?.crewMember}
						driverName={driverName}
						driverId={driverId}
						isProblematic={isProblematic}
						hasBreakdown={assignment?.breakdown !== null}
					/>
				</div>
				<div className={cx(styles.block, { [styles.disabled]: isInWidgetMode })}>
					<WorkAssignmentActionMenu
						items={actionMenuItems}
						isLoading={isActionMenuLoading}
						active={isActionMenuActive}
						onToggle={(nextState) => onActionMenuToggle(nextState)}
					>
						<Button
							aria-label="Open action menu"
							appearance="link"
							display="square"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
						>
							<Icon InlineIcon={IconKebab} />
						</Button>
					</WorkAssignmentActionMenu>
				</div>
			</div>
			{(isExpanded || isInWidgetMode) && (
				<>
					<div className={styles.divider}>
						<Divider />
					</div>
					<Stations
						stationData={{
							stations,
							stationActuals,
							rideItems,
							breakdown: assignment.breakdown,
							isProblematic,
						}}
						reconciliation={{ sequences, sequenceGroupId, firstStation, lastStation }}
						onCopyRideId={onCopyRideId}
						onSaveSuccess={onReconcileSuccess}
					/>
					{isInWidgetMode && (
						<EditAssignmentFlat onSubmit={handlePopupSubmit} defaultValues={popupDefaultValues} />
					)}
				</>
			)}
		</div>
	);
};

export default WorkAssignmentCard;
